import type { ColDef, GridApi, GridOptions, ICellRendererParams } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CheckboxWithLabel } from '@/components/lemmala-design-system/checkbox/checkbox'
import { TABLE_HEADER_HEIGHTS } from '@/components/lemmala-design-system/table/consts'
import { usePersistedGrouped } from '@/components/lemmala-design-system/table/hooks/use-persisted-grouped'

import { AgDataTable, type AgDataTableProps } from './ag-data-table'
import { GroupHeaderCell } from './components/group-header-cell'
import { useGroupableRows } from './hooks/use-groupable-rows'
import type { RowClickMeta } from './types'
import type { Group, RowWithGroupingMetadata } from './types'

export type AgCollapseChildrenTableProps<T extends { id: string }> = Omit<
  AgDataTableProps<RowWithGroupingMetadata<T>>,
  'rowData' | 'grouping'
> & {
  data: T[]
  toGroups: (data: T[]) => Group<T>[]
  /** When provided, renders a checkbox toggle in the filter bar and manages enabled state internally. Without it, grouping is always enabled. */
  toggleLabel?: string
  /** Called when the user toggles grouping. Not called when persisted state is restored. */
  onGroupedChange?: (grouped: boolean) => void
  /** When provided, automatically renders a GroupHeaderCell on the first column for group header rows. */
  groupHeader?: {
    getLabel: (row: RowWithGroupingMetadata<T>) => string
    className?: string
  }
}

export function AgCollapseChildrenTable<T extends { id: string }>({
  data,
  toGroups,
  toggleLabel,
  onGroupedChange,
  groupHeader,
  columnDefs,
  search,
  gridOptions,
  onRowClick,
  onGridReady,
  customFilterBarLeft,
  ...rest
}: AgCollapseChildrenTableProps<T>) {
  const prefix = rest.urlSync?.prefix
  const [internalEnabled, setInternalEnabled] = usePersistedGrouped({
    prefix,
    defaultGrouped: false,
    enabled: !!toggleLabel && !!prefix,
  })
  const enabled = toggleLabel ? internalEnabled : true

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const gridApiRef = useRef<GridApi | null>(null)

  // AG Grid caches getRowClass results — force a redraw when enabled changes so row classes update.
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setExpandedGroups(new Set())
    gridApiRef.current?.redrawRows()
  }, [enabled])

  const handleGridReady = useCallback(
    (api: GridApi<RowWithGroupingMetadata<T>>) => {
      gridApiRef.current = api
      onGridReady?.(api)
    },
    [onGridReady],
  )

  const toggleGroupExpanded = useCallback((id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const { processedData, wrapColumnDefs, wrapSearchMatch, postSortRows, groupingConfig } =
    useGroupableRows<T>({ data, toGroups, expandedGroups, enabled })

  const columnDefsWithGroupHeader = useMemo(() => {
    if (!groupHeader) return columnDefs

    const groupHeaderCol: ColDef<RowWithGroupingMetadata<T>> = {
      colId: '__groupHeader',
      maxWidth: 0,
      suppressColumnsToolPanel: true,
      cellRenderer: (params: ICellRendererParams<RowWithGroupingMetadata<T>>) => {
        if (!params.data?.isGroupHeader) return null
        return (
          <GroupHeaderCell
            label={groupHeader.getLabel(params.data)}
            isExpanded={!!params.data.isExpanded}
            className={groupHeader.className}
          />
        )
      },
      colSpan: (params) => {
        if (params.data?.isGroupHeader) return columnDefs.length + 1
        return 1
      },
    }

    return [groupHeaderCol, ...columnDefs]
  }, [columnDefs, groupHeader])

  const wrappedColumnDefs = useMemo(
    () => wrapColumnDefs(columnDefsWithGroupHeader),
    [wrapColumnDefs, columnDefsWithGroupHeader],
  )

  const wrappedSearch = useMemo(() => {
    if (!search?.doesRowMatch) return search
    return { ...search, doesRowMatch: wrapSearchMatch(search.doesRowMatch) }
  }, [search, wrapSearchMatch])

  const mergedGridOptions = useMemo<GridOptions<RowWithGroupingMetadata<T>>>(
    () => ({
      ...gridOptions,
      getRowClass: (params) => {
        const row = params.data
        let groupClass = ''
        if (enabled) {
          if (row?.isGroupParent) groupClass = 'ag-row-group-parent'
          else if (row?.isGroupHeader) groupClass = 'ag-row-group-header cursor-pointer'
          else if (row?.isGroupChild) groupClass = 'ag-row-group-child'
        }
        const extra =
          typeof gridOptions?.getRowClass === 'function'
            ? gridOptions.getRowClass(params)
            : (gridOptions?.getRowClass ?? '')
        return [groupClass, extra].filter(Boolean).join(' ')
      },
      getRowHeight: (params) => {
        if (params.data?.isGroupHeader) return TABLE_HEADER_HEIGHTS.group
        return gridOptions?.getRowHeight?.(params)
      },
      getRowStyle: (params) => {
        const row = params.data
        if (row?.isGroupParent || (row?.isGroupHeader && row?.isExpanded)) {
          return { borderBottom: 'none' }
        }
        return gridOptions?.getRowStyle?.(params)
      },
      postSortRows,
    }),
    [gridOptions, postSortRows, enabled],
  )

  const handleRowClick = useCallback(
    (row: RowWithGroupingMetadata<T>, meta: RowClickMeta) => {
      if (row.isGroupHeader) {
        const groupId = row.groupId ?? row.id.replace('-group-header', '')
        toggleGroupExpanded(groupId)
        return
      }
      onRowClick?.(row, meta)
    },
    [onRowClick, toggleGroupExpanded],
  )

  const filterBarLeft = toggleLabel ? (
    <>
      <CheckboxWithLabel
        checked={enabled}
        onCheckedChange={(checked) => {
          const next = !!checked
          setInternalEnabled(next)
          onGroupedChange?.(next)
        }}
      >
        {toggleLabel}
      </CheckboxWithLabel>
      {customFilterBarLeft}
    </>
  ) : (
    customFilterBarLeft
  )

  return (
    <AgDataTable<RowWithGroupingMetadata<T>>
      {...rest}
      rowData={processedData}
      columnDefs={wrappedColumnDefs}
      search={wrappedSearch}
      gridOptions={mergedGridOptions}
      grouping={enabled ? groupingConfig : undefined}
      onRowClick={handleRowClick}
      onGridReady={handleGridReady}
      customFilterBarLeft={filterBarLeft}
    />
  )
}
